/**
 * Settings -> Printing: which printer each kind of printing goes to, whether
 * the receipt prints by itself after a sale, and the cash drawer.
 *
 * A shop's settings, so every till in the shop follows them. The printers are
 * named as Windows names them on the till PC; a shop with two tills gives its
 * two receipt printers the same name in Windows, which is the usual case anyway
 * when both came from the same box.
 */
import { useCallback, useEffect, useState } from 'react';
import { useT } from '../../core/i18n/index.js';
import type { StringKey } from '../../core/i18n/en.js';
import { useToast } from '../../core/state/toast.js';
import { useSettings } from '../../core/state/settings.js';
import {
  DEFAULT_HELPER_URL,
  HelperError,
  helperHealth,
  helperPrinters,
  helperProblem,
  helperUrl,
  printRollViaHelper,
  setHelperUrl,
  type HelperHealth,
  type HelperPrinter,
} from '../../core/printing.js';
import { barcodeSvg, escapeHtml } from '../../core/print.js';
import { toBase64, kickJob } from '../../core/escpos.js';
import { Button, Field, Pill } from '../../core/ui/ui.js';
import { SaveBar, useDraft } from './Admin.js';

export function PrintingSection() {
  const t = useT();
  const toast = useToast();
  const { settings } = useSettings();
  const { draft, setDraft, dirty, busy, commit } = useDraft();
  const [health, setHealth] = useState<HelperHealth | null>(null);
  const [printers, setPrinters] = useState<HelperPrinter[] | null>(null);
  const [url, setUrl] = useState(helperUrl);
  const [testing, setTesting] = useState<'' | 'receipt' | 'drawer'>('');
  const viaHelper = draft.printVia === 'helper';
  const set = (patch: Partial<typeof draft>) => setDraft({ ...draft, ...patch });

  const check = useCallback(async () => {
    setHealth(null);
    setPrinters(null);
    const h = await helperHealth(url);
    setHealth(h);
    if (h.ok) setPrinters((await helperPrinters()).printers);
  }, [url]);

  useEffect(() => {
    if (viaHelper) void check();
  }, [viaHelper, check]);

  const why = (e: unknown) => helperProblem(e instanceof HelperError ? e.code : String(e), t);

  const testReceipt = async () => {
    setTesting('receipt');
    const name = settings.storeNameAr || settings.storeNameEn || 'POS';
    const html = `<style>body{text-align:center;font-size:12px}.b{font-size:18px;font-weight:700;margin:2mm 0}</style>
      <div class="b">${escapeHtml(name)}</div>
      <div>${escapeHtml(t('print.testLine'))}</div>
      <div dir="rtl">طباعة تجريبية - العربية تظهر كما هي على الشاشة</div>
      <div>Test print - ${escapeHtml(new Date().toLocaleString('en-GB'))}</div>
      <div style="margin-top:2mm">${barcodeSvg('2000000000015', { height: 36 })}</div>`;
    try {
      await printRollViaHelper(html, { printer: draft.receiptPrinter, paper: draft.receiptPaper });
      toast.ok(t('print.testSent'));
    } catch (e) {
      toast.err(why(e));
    } finally {
      setTesting('');
    }
  };

  const testDrawer = async () => {
    setTesting('drawer');
    try {
      const res = await fetch(`${url.replace(/\/$/, '')}/print`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ printer: draft.receiptPrinter, base64: toBase64(kickJob(draft.drawerPin)) }),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || data.ok === false) throw new HelperError(data.error ?? 'failed');
      toast.ok(t('print.drawerSent'));
    } catch (e) {
      toast.err(why(e instanceof TypeError ? new HelperError('not_running') : e));
    } finally {
      setTesting('');
    }
  };

  const printerBox = (key: 'receiptPrinter' | 'labelPrinter', emptyLabel: string) =>
    printers && printers.length ? (
      <select value={draft[key]} onChange={(e) => set({ [key]: e.target.value })}>
        <option value="">{emptyLabel}</option>
        {printers.map((p) => (
          <option key={p.name} value={p.name}>
            {p.name}
            {p.port ? ` (${p.port})` : ''}
          </option>
        ))}
        {draft[key] && !printers.some((p) => p.name === draft[key]) ? (
          <option value={draft[key]}>{`${draft[key]} - ${t('print.notOnThisPc')}`}</option>
        ) : null}
      </select>
    ) : (
      <input dir="ltr" value={draft[key]} placeholder="POS-80" onChange={(e) => set({ [key]: e.target.value })} />
    );

  const toggle = (key: 'autoPrintReceipt' | 'drawerEnabled' | 'drawerOnCash', title: StringKey, desc?: StringKey) => (
    <label className="switchrow">
      <div className="grow">
        <div className="t">{t(title)}</div>
        {desc ? <div className="d">{t(desc)}</div> : null}
      </div>
      <input
        type="checkbox"
        checked={!!draft[key]}
        onChange={(e) => set({ [key]: e.target.checked })}
        style={{ width: 22, height: 22, accentColor: 'var(--accent)' }}
      />
    </label>
  );

  return (
    <div className="card">
      <h2>{t('print.title')}</h2>
      <p className="small muted" style={{ marginTop: 0 }}>{t('print.intro')}</p>

      <Field label={t('print.how')}>
        <div className="btn-row">
          <Button variant={viaHelper ? 'default' : 'primary'} onClick={() => set({ printVia: 'window' })}>
            {t('print.viaWindow')}
          </Button>
          <Button variant={viaHelper ? 'primary' : 'default'} onClick={() => set({ printVia: 'helper' })}>
            {t('print.viaHelper')}
          </Button>
        </div>
      </Field>

      {!viaHelper ? (
        <p className="small">{t('print.windowHow')}</p>
      ) : (
        <div className="col" style={{ gap: 'var(--s-3)' }}>
          <div className="row wrap" style={{ gap: 'var(--s-2)', alignItems: 'center' }}>
            {health === null ? (
              <Pill>{t('print.checking')}</Pill>
            ) : health.ok ? (
              <Pill kind="ok">{t('print.running', { version: health.version ?? '' })}</Pill>
            ) : (
              <Pill kind="danger">{t('print.notRunningShort')}</Pill>
            )}
            {health?.ok && !health.canPrint ? <Pill kind="warn">{t('print.oldHelper')}</Pill> : null}
            <div className="spacer" />
            <Button size="sm" variant="ghost" onClick={() => void check()}>{t('print.checkAgain')}</Button>
          </div>

          {health && (!health.ok || !health.canPrint) ? (
            <div className="drawer-install">
              <p className="small" style={{ margin: 0 }}>{t('print.installHow')}</p>
              <a className="btn" href="/downloads/print-helper.zip" download>{t('print.download')}</a>
            </div>
          ) : null}

          <div className="grid c2">
            <Field label={t('print.receiptPrinter')} hint={printers && !printers.length ? t('print.typePrinter') : t('print.receiptPrinterHint')}>
              {printerBox('receiptPrinter', '—')}
            </Field>
            <Field label={t('print.paper')}>
              <div className="btn-row">
                {(['80', '58'] as const).map((w) => (
                  <Button key={w} variant={draft.receiptPaper === w ? 'primary' : 'default'} onClick={() => set({ receiptPaper: w })}>
                    {w} mm
                  </Button>
                ))}
              </div>
            </Field>
            <Field label={t('print.labelPrinter')} hint={t('print.labelPrinterHint')}>
              {printerBox('labelPrinter', t('print.labelsInWindow'))}
            </Field>
          </div>
          <div className="row wrap" style={{ gap: 'var(--s-2)' }}>
            <Button busy={testing === 'receipt'} disabled={!draft.receiptPrinter || !health?.ok} onClick={() => void testReceipt()}>
              {t('print.testReceipt')}
            </Button>
          </div>
          <p className="xsmall muted" style={{ margin: 0 }}>{t('print.a4Note')}</p>
        </div>
      )}

      <div style={{ marginBlockStart: 'var(--s-4)' }}>
        {toggle('autoPrintReceipt', 'print.auto', viaHelper ? 'print.autoHelper' : 'print.autoWindow')}
        {toggle('drawerEnabled', 'print.drawer', 'print.drawerNote')}
        {draft.drawerEnabled ? (
          <>
            {toggle('drawerOnCash', 'print.drawerOnCash', viaHelper ? undefined : 'print.drawerOnCashWindow')}
            <div className="row wrap" style={{ gap: 'var(--s-3)', alignItems: 'flex-end', marginBlockStart: 'var(--s-3)' }}>
              <Field label={t('print.pin')} hint={t('print.pinHint')}>
                <select value={draft.drawerPin} onChange={(e) => set({ drawerPin: e.target.value as '2' | '5' | 'both' })}>
                  <option value="2">2</option>
                  <option value="5">5</option>
                  <option value="both">{t('print.pinBoth')}</option>
                </select>
              </Field>
              {viaHelper ? (
                <Button busy={testing === 'drawer'} disabled={!draft.receiptPrinter || !health?.ok} onClick={() => void testDrawer()}>
                  {t('print.testDrawer')}
                </Button>
              ) : null}
            </div>
          </>
        ) : null}
      </div>

      {viaHelper ? (
        <details className="small" style={{ marginBlockStart: 'var(--s-4)' }}>
          <summary className="muted">{t('print.advanced')}</summary>
          <Field label={t('print.helperUrl')} hint={t('print.helperUrlHint')}>
            <input
              dir="ltr"
              value={url}
              placeholder={DEFAULT_HELPER_URL}
              onChange={(e) => {
                setUrl(e.target.value || DEFAULT_HELPER_URL);
                setHelperUrl(e.target.value);
              }}
            />
          </Field>
        </details>
      ) : null}

      <SaveBar dirty={dirty} busy={busy} onSave={() => void commit()} />
    </div>
  );
}
